<template>
	<div class="auth-modal" v-if="isVisible">
		<div class="edit-profile-form-container">
			<div v-if="step === 1">
				<h2>Edit Profile</h2>
				<form @submit.prevent="updateProfile">
					<div v-if="error" class="error-message">{{ error }}</div>
					<div class="form-group">
						<label for="firstName">First Name:</label>
						<input type="text" id="firstName" v-model="user.firstName" required>
					</div>
					<div class="form-group">
						<label for="familyName">Family Name:</label>
						<input type="text" id="familyName" v-model="user.familyName" required>
					</div>
					<div class="form-group">
						<label for="phoneNumber">Phone Number:</label>
						<div class="input-with-status">
							<input type="text" id="phoneNumber" v-model="user.phoneNumber">
							<span v-if="user.phoneNumber === authStatus.phoneNumber && authStatus.isPhoneVerified" class="status-icon verified" title="Verified">✅</span>
							<span v-else-if="user.phoneNumber" class="status-icon unverified" title="Not Verified">⚠️</span>
						</div>
					</div>
					<div class="form-group">
						<label for="email">Email:</label>
						<div class="input-with-status">
							<input type="email" id="email" v-model="user.email" required>
							<span v-if="user.email === authStatus.email && authStatus.isEmailVerified" class="status-icon verified" title="Verified">✅</span>
							<span v-else-if="user.email" class="status-icon unverified" title="Not Verified">⚠️</span>
						</div>
					</div>
					<div class="button-row">
						<button type="submit" class="user-modal-submit-btn green" :disabled="isUpdating">
							{{ isUpdating ? 'Updating...' : 'Update Profile' }}
						</button>
						<button type="button" @click="cancelEdit" class="cancel-btn" :disabled="isUpdating">Cancel</button>
					</div>
				</form>
			</div>
			<div v-if="step === 2">
				<VerificationStep
					:type="verificationType"
					:target="verificationTarget"
					:error="error"
					:loading="isUpdating"
					showSkip
					@confirm="handleVerificationSubmit"
					@skip="skipVerification"
					@back="step = 1"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import GetClientAuthStatusQuery from '../graphql/user/GetClientAuthStatusQuery.gql';
import UpdateUserMutation from '../graphql/user/UpdateUserMutation.gql';
import VerifyUser from '../graphql/user/VerifyUserMutation.gql';
import VerificationStep from './VerificationStep.vue';

export default {
	name: 'EditProfileModal',
	components: {
		VerificationStep
	},
	props: {
		isVisible: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			user: {
				firstName: '',
				familyName: '',
				phoneNumber: '',
				email: '',
			},
			isUpdating: false,
			error: '',
			step: 1,
			verificationType: 'email',
			verificationTarget: ''
		};
	},
	apollo: {
		authStatus: {
			query: GetClientAuthStatusQuery,
			fetchPolicy: 'cache-only',
		}
	},
	created() {
		this.user.firstName = this.authStatus.firstName;
		this.user.familyName = this.authStatus.familyName;
		this.user.phoneNumber = this.authStatus.phoneNumber;
		this.user.email = this.authStatus.email;
		window.addEventListener('keydown', this.handleKeydown);
	},
	beforeUnmount() {
		window.removeEventListener('keydown', this.handleKeydown);
	},
	methods: {
		handleKeydown(e) {
			if (e.key === 'Escape' && this.isVisible) {
				this.cancelEdit();
			}
		},
		cancelEdit() {
			this.$emit('cancel');
		},
		async updateProfile() {
			if (!this.user.email || !this.user.email.includes('@')) {
				this.error = 'A valid email address is required.';
				return;
			}

			this.isUpdating = true;
			this.error = '';
			try {
				const { data } = await this.$apollo.mutate({
					mutation: UpdateUserMutation,
					variables: {
						userID: this.authStatus.userID,
						firstName: this.user.firstName,
						familyName: this.user.familyName,
						phoneNumber: this.user.phoneNumber,
						email: this.user.email,
					}
				});

				if (data && data.UpdateUser) {
					const result = data.UpdateUser;
					this.$emit('auth-update', result.user);

					if (result.emailCodeSent) {
						this.verificationType = 'email';
						this.verificationTarget = this.user.email;
						this.step = 2;
					} else if (result.phoneCodeSent) {
						this.verificationType = 'phone';
						this.verificationTarget = this.user.phoneNumber;
						this.step = 2;
					} else {
						this.$emit('success');
					}
				}
			} catch (err) {
				console.error('Update failed:', err);
				this.error = err.message;
			} finally {
				this.isUpdating = false;
			}
		},
		async handleVerificationSubmit(payload) {
			const { code, type } = payload;
			this.error = '';
			this.isUpdating = true;
			try {
				const { data } = await this.$apollo.mutate({
					mutation: VerifyUser,
					variables: {
						userID: this.authStatus.userID,
						code: code,
						type: type
					}
				});

				if (data && data.VerifyUser) {
					const result = data.VerifyUser;
					this.$emit('auth-update', result.user);

					// Sequential Check:
					// If we just verified email but phone now needs verification
					if (type === 'email' && result.user.phoneNumber && !result.user.isPhoneVerified) {
						this.verificationType = 'phone';
						this.verificationTarget = result.user.phoneNumber;
						this.step = 2;
					} else {
						this.step = 1;
						this.$emit('success');
					}
				}
			} catch (err) {
				console.error('Verification failed:', err);
				this.error = 'Invalid code. Please try again.';
			} finally {
				this.isUpdating = false;
			}
		},
		skipVerification() {
			this.step = 1;
			this.$emit('success');
		}
	}
};
</script>

<style scoped>
.input-with-status {
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
}

.input-with-status input {
	flex: 1;
}

.status-icon {
	font-size: 1.2rem;
}

.status-icon.verified {
	color: #4CAF50;
	cursor: default;
}

.status-icon.unverified {
	color: #FFC107;
	cursor: default;
}
</style>
<style src="@/assets/user-shared.css"/>
